import type { Socket } from 'socket.io-client'
import { SOCKET_EVENTS } from '@shared/index'

const releases = new WeakMap<Socket, () => void>()

/**
 * After the server closes an idle socket, stay disconnected until the player
 * is looking at the page again. Chat, duel, spectate and the room list all
 * share this so a dropped tab does not reconnect itself in the background.
 */
export function attachPresenceHold(socket: Socket): void {
  let held = false
  let released = false

  const release = (): void => {
    if (released) return
    released = true
    held = false
    document.removeEventListener('visibilitychange', onVisible)
    window.removeEventListener('pointerdown', onInput)
    window.removeEventListener('pointermove', onInput)
    window.removeEventListener('keydown', onInput)
    window.removeEventListener('touchstart', onInput)
  }

  const resume = (): void => {
    if (released || !held) return
    if (document.visibilityState === 'hidden') return
    held = false
    socket.io.reconnection(true)
    if (!socket.connected) socket.connect()
  }

  const onVisible = (): void => {
    resume()
  }

  const onInput = (): void => {
    resume()
  }

  socket.on(SOCKET_EVENTS.PRESENCE_SLEEP, () => {
    if (released) return
    held = true
    socket.io.reconnection(false)
  })

  document.addEventListener('visibilitychange', onVisible)
  window.addEventListener('pointerdown', onInput)
  window.addEventListener('pointermove', onInput)
  window.addEventListener('keydown', onInput)
  window.addEventListener('touchstart', onInput)
  releases.set(socket, release)
}

/** Drops the page listeners. Call from the owning client's dispose. */
export function releasePresenceHold(socket: Socket): void {
  releases.get(socket)?.()
  releases.delete(socket)
}
